import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';

type DetectionStats = {
  userAgent: string;
  dpr: number;

  requested: {
    width?: string | number;
    height?: string | number;
    frameRate?: string | number;
    resizeMode?: string;
  };

  settings: {
    width?: number;
    height?: number;
    frameRate?: number;
    aspectRatio?: number;
    facingMode?: string;
    resizeMode?: string;
  };

  capabilities: {
    torch?: boolean;
    zoom?: { min?: number; max?: number; step?: number };
    focusMode?: string[] | undefined;
  };

  video: {
    videoWidth: number;
    videoHeight: number;
    clientWidth: number;
    clientHeight: number;
    viewportHeight: number;
  };

  // rVFC cadence
  avgVfcIntervalMs: number;
  lastVfcIntervalMs: number;
  vfcSamples: number;

  // Worker timings (rolling)
  avgPrepMs: number; // ROI/resize inside worker
  lastPrepMs: number;

  avgDetectMs: number; // detector.detect() inside worker
  lastDetectMs: number;
  detectSamples: number;

  barcodesFound: number;

  // throttle & input size
  detectThrottleMs: number;
  autoDownscale: boolean;
  maxDetectPixels: number;

  effectiveDetectWidth: number;
  effectiveDetectHeight: number;

  // worker status
  workerReady: boolean;
  workerError?: string;

  // ROI
  roiEnabled: boolean;
  roi: { x: number; y: number; width: number; height: number }; // normalized
};

@Component
export default class BarcodeScanner extends Vue {
  // NOTE: We run BarcodeDetector in a worker now
  barcodeDetector: BarcodeDetector | undefined;

  $refs!: Vue['$refs'] & {
    scannerVideo: HTMLVideoElement;
  };

  @Prop({ default: false }) readonly enabled!: boolean;

  cameraStream: MediaStream | null = null;
  imageCapture: ImageCapture | null = null;

  // Worker plumbing
  worker: Worker | null = null;
  nextDetectId = 0;
  detectInFlight = false;

  // Session/generation to ignore stale results
  sessionId = 0;

  torchEnabled: boolean = false;
  torchAvailable: boolean = false;

  detectBarcodeRateMs: number = 300;
  zoomStepRateMs: number = 10;

  zoomAvailable: boolean = true;
  zoomMin: number = 1;
  zoomMax: number = 5;
  zoomStep: number = 0.5;

  readonly ZOOM_STORE_KEY = 'barcode-scanner-camera-zoom';

  zoomLevel: number = 1;
  initialPinchDistance: number = 0;

  viewportHeight = 420; //nice

  // --- Secret Debug UI -------------------------------------------------

  private DEBUG_TAP_THRESHOLD = 7;
  private DEBUG_TAP_WINDOW_MS = 1500;
  private debugTapCount = 0;
  private debugTapTimer: any = null;

  debugVisible: boolean = false;

  // Optional: force capture size for experiments (null = negotiated)
  private debugForceSize: null | { width: number; height: number } = null;

  // Detection budget & ROI
  private detectAutoDownscale = true;
  private detectMaxPixels = 1280 * 720; // ~0.9 MP
  private useCenterRoi = true;
  private roi = { x: 0.15, y: 0.25, width: 0.7, height: 0.5 }; // centered band

  private lastVfcNow = 0;
  lastDetectStart = 0;

  debugStats: DetectionStats = {
    userAgent: navigator.userAgent,
    dpr: window.devicePixelRatio || 1,
    requested: {},
    settings: {},
    capabilities: {},
    video: {
      videoWidth: 0,
      videoHeight: 0,
      clientWidth: 0,
      clientHeight: 0,
      viewportHeight: this.viewportHeight
    },
    avgVfcIntervalMs: 0,
    lastVfcIntervalMs: 0,
    vfcSamples: 0,
    avgPrepMs: 0,
    lastPrepMs: 0,
    avgDetectMs: 0,
    lastDetectMs: 0,
    detectSamples: 0,
    barcodesFound: 0,
    detectThrottleMs: this.detectBarcodeRateMs,
    autoDownscale: this.detectAutoDownscale,
    maxDetectPixels: this.detectMaxPixels,
    effectiveDetectWidth: 0,
    effectiveDetectHeight: 0,
    workerReady: false,
    roiEnabled: this.useCenterRoi,
    roi: this.roi
  };

  // --------------------------------------------------------------------

  beforeMount() {
    if ('BarcodeDetector' in window) {
      this.barcodeDetector = new BarcodeDetector({ formats: ['code_128'] });
    } else {
      this.barcodeDetector = undefined;
    }

    this.onEnabledChange = this.onEnabledChange.bind(this);
    this.onVideoFrame = this.onVideoFrame.bind(this);
  }

  mounted() {
    const storedZoom = window.localStorage.getItem(this.ZOOM_STORE_KEY);
    if (storedZoom) {
      this.zoomLevel = parseFloat(storedZoom) ?? this.zoomMin ?? 1;
    }
    this.onEnabledChange();
  }

  beforeDestroy() {
    this.closeScanner(true);
    // Reset debug unlock each time the component is closed
    this.debugVisible = false;
    this.debugTapCount = 0;
    clearTimeout(this.debugTapTimer);
  }

  // ---- Secret tap handler (top-right zone) ----------------------------
  onSecretTap() {
    if (this.debugVisible) return;

    this.debugTapCount++;
    if (!this.debugTapTimer) {
      this.debugTapTimer = setTimeout(() => {
        this.debugTapCount = 0;
        this.debugTapTimer = null;
      }, this.DEBUG_TAP_WINDOW_MS);
    }
    if (this.debugTapCount >= this.DEBUG_TAP_THRESHOLD) {
      clearTimeout(this.debugTapTimer);
      this.debugTapTimer = null;
      this.debugTapCount = 0;
      this.debugVisible = true;
      this.snapshotStats();
    }
  }

  hideDebug() {
    this.debugVisible = false;
  }

  // --------------------------------------------------------------------

  private startNewSession() {
    // Invalidate any in-flight callbacks/results
    this.sessionId = (this.sessionId + 1) | 0;
    this.detectInFlight = false;
    this.lastDetectStart = 0;
    this.lastVfcNow = 0;
    this.debugStats.workerReady = false;
    this.debugStats.workerError = undefined;
  }

  initScanner() {
    this.startNewSession();

    // Build constraints; optionally force size via debug panel
    const forced = this.debugForceSize;
    const requestedWidth = forced?.width ?? { min: 600, ideal: 1080, max: 1440 };
    const requestedHeight = forced?.height ?? { min: 600, ideal: 1080, max: 1440 };

    const constraints: MediaStreamConstraints = {
      video: {
        facingMode: { ideal: 'environment' },
        width: requestedWidth as any,
        height: requestedHeight as any,
        frameRate: { ideal: 30, max: 30 },
        // Chrome-only, avoids crop-and-scale when it can
        ...{ resizeMode: 'none' }
      }
    };

    this.debugStats.requested = {
      width: typeof requestedWidth === 'object' ? JSON.stringify(requestedWidth) : requestedWidth,
      height: typeof requestedHeight === 'object' ? JSON.stringify(requestedHeight) : requestedHeight,
      frameRate: 'ideal 30 / max 30',
      resizeMode: 'none'
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((mediaStream) => {
        const currentSession = this.sessionId;

        this.cameraStream = mediaStream;
        const videoEl = this.$refs.scannerVideo;

        // Important: clear old handlers before setting new ones
        videoEl.onloadedmetadata = null as any;
        videoEl.oncanplay = null as any;

        videoEl.srcObject = mediaStream;
        videoEl.onloadedmetadata = () => {
          // guard against stale callbacks
          if (currentSession !== this.sessionId) return;
          videoEl.play();
        };

        // Capabilities and negotiated settings
        const track = mediaStream.getVideoTracks()[0];
        const caps = track.getCapabilities ? track.getCapabilities() : ({} as any);
        const settings = track.getSettings ? track.getSettings() : ({} as any);
        this.torchAvailable = !!caps.torch;
        this.zoomAvailable = !!caps.zoom;

        this.debugStats.capabilities = {
          torch: !!caps.torch,
          zoom: caps.zoom ? { min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step } : undefined,
          focusMode: caps.focusMode
        };
        this.debugStats.settings = {
          width: settings.width,
          height: settings.height,
          frameRate: settings.frameRate,
          aspectRatio: settings.aspectRatio,
          facingMode: settings.facingMode,
          resizeMode: (settings as any).resizeMode
        };

        // Prepare ImageCapture for frame grabs
        try {
          this.imageCapture = new (window as any).ImageCapture(track);
        } catch (e) {
          this.emitFailedToLoad('ImageCapture not supported on this device');
          this.imageCapture = null;
        }

        // Init worker for THIS session
        this.initWorker(currentSession);

        videoEl.oncanplay = () => {
          if (currentSession !== this.sessionId) return;
          this.viewportHeight = (videoEl.videoHeight / videoEl.videoWidth) * videoEl.clientWidth;
          this.debugStats.video.viewportHeight = this.viewportHeight;
          this.lastVfcNow = 0;
          videoEl.requestVideoFrameCallback(this.onVideoFrame as any);
        };
      })
      .catch((err) => {
        this.emitFailedToLoad(err);
      });
  }

  initWorker(session: number) {
    // Terminate any previous worker
    if (this.worker) {
      try {
        this.worker.terminate();
      } catch {
        console.warn('Failed to terminate previous worker');
      }
      this.worker = null;
    }
    try {
      this.worker = new Worker(new URL('./barcode.worker.ts', import.meta.url), { type: 'module' });

      this.worker.onmessage = (evt: MessageEvent) => {
        const data: any = evt.data;

        // Ignore stale results from a previous session
        if (typeof data?.session === 'number' && data.session !== this.sessionId) {
          return;
        }

        if (data?.type === 'init-ok') {
          this.debugStats.workerReady = true;
          this.debugStats.workerError = undefined;
          return;
        }
        if (data?.type === 'result') {
          // Update stats
          this.debugStats.lastDetectMs = data.detectMs;
          this.debugStats.detectSamples++;
          const m = Math.min(this.debugStats.detectSamples, 60);
          this.debugStats.avgDetectMs = this.debugStats.avgDetectMs + (data.detectMs - this.debugStats.avgDetectMs) / m;

          this.debugStats.lastPrepMs = data.prepMs;
          const k = Math.min(this.debugStats.detectSamples, 60);
          this.debugStats.avgPrepMs = this.debugStats.avgPrepMs + (data.prepMs - this.debugStats.avgPrepMs) / k;

          this.debugStats.effectiveDetectWidth = data.width;
          this.debugStats.effectiveDetectHeight = data.height;

          if (Array.isArray(data.values) && data.values.length) {
            this.debugStats.barcodesFound += data.values.length;
            this.validateCode(String(data.values[0]));
          }
          this.detectInFlight = false;
          return;
        }
        if (data?.type === 'error') {
          this.debugStats.workerError = data.error || 'Worker error';
          this.detectInFlight = false; // critical: release the guard on errors
          console.error('Barcode worker error:', data.error);
          return;
        }
      };

      // Init with formats (session bound)
      this.worker.postMessage({ type: 'init', session, formats: ['code_128'] });
    } catch (err: any) {
      this.debugStats.workerError = err?.message ?? String(err);
      this.worker = null;
    }
  }

  // Snapshot static-ish stats
  snapshotStats() {
    const videoEl = this.$refs.scannerVideo;
    if (!videoEl) return;

    this.debugStats.userAgent = navigator.userAgent;
    this.debugStats.dpr = window.devicePixelRatio || 1;

    this.debugStats.video = {
      videoWidth: videoEl.videoWidth,
      videoHeight: videoEl.videoHeight,
      clientWidth: videoEl.clientWidth,
      clientHeight: videoEl.clientHeight,
      viewportHeight: this.viewportHeight
    };

    this.debugStats.detectThrottleMs = this.detectBarcodeRateMs;
    this.debugStats.autoDownscale = this.detectAutoDownscale;
    this.debugStats.maxDetectPixels = this.detectMaxPixels;
    this.debugStats.roiEnabled = this.useCenterRoi;
    this.debugStats.roi = this.roi;
  }

  async sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // rVFC-driven render loop — non-blocking
  async onVideoFrame(now?: number) {
    const videoEl = this.$refs.scannerVideo;
    if (!videoEl) return;

    // schedule next rVFC immediately
    if (this.enabled) {
      videoEl.requestVideoFrameCallback(this.onVideoFrame as any);
    }

    // Update rVFC cadence stats
    if (typeof now === 'number') {
      if (this.lastVfcNow > 0) {
        const interval = now - this.lastVfcNow;
        this.debugStats.lastVfcIntervalMs = interval;
        this.debugStats.vfcSamples++;
        const n = Math.min(this.debugStats.vfcSamples, 60);
        this.debugStats.avgVfcIntervalMs =
          this.debugStats.avgVfcIntervalMs + (interval - this.debugStats.avgVfcIntervalMs) / n;
      }
      this.lastVfcNow = now;
    }

    // Update overlay video dims
    if (this.debugVisible) {
      this.debugStats.video.videoWidth = videoEl.videoWidth;
      this.debugStats.video.videoHeight = videoEl.videoHeight;
      this.debugStats.video.clientWidth = videoEl.clientWidth;
      this.debugStats.video.clientHeight = videoEl.clientHeight;
    }

    // Throttle detection; do not block UI
    if (!this.imageCapture || !this.worker || !this.debugStats.workerReady) return;
    const nowMs = performance.now();
    if (this.detectInFlight || nowMs - this.lastDetectStart < this.detectBarcodeRateMs) return;

    this.detectInFlight = true;
    this.lastDetectStart = nowMs;

    try {
      const bitmap: ImageBitmap = await(this.imageCapture as any).grabFrame();
      const session = this.sessionId;
      this.worker.postMessage(
        {
          type: 'detect',
          id: ++this.nextDetectId,
          session,
          bitmap,
          maxPixels: this.detectAutoDownscale ? this.detectMaxPixels : bitmap.width * bitmap.height,
          roi: this.useCenterRoi ? this.roi : null,
          formats: ['code_128']
        },
        [bitmap]
      ); // transfer ownership
    } catch (err) {
      // If grabFrame fails (e.g., track ended), release the guard so we can recover
      this.detectInFlight = false;
      this.emitFailedToLoad(err);
    }
  }

  closeScanner(invalidateSession = false) {
    // Invalidate session to ignore late worker replies
    if (invalidateSession) this.sessionId = (this.sessionId + 1) | 0;

    // Release detection guard so next enable can proceed
    this.detectInFlight = false;
    this.lastDetectStart = 0;
    this.lastVfcNow = 0;

    try {
      this.cameraStream?.getTracks().forEach((track) => track.stop());
    } catch {
      console.warn('Failed to stop camera tracks');
    }
    this.cameraStream = null;
    this.imageCapture = null;

    // Clear the video element to ensure oncanplay fires next time
    const videoEl = this.$refs.scannerVideo;
    if (videoEl) {
      try {
        videoEl.pause();
      } catch {
        console.warn('Failed to pause video element');
      }
      videoEl.oncanplay = null as any;
      videoEl.onloadedmetadata = null as any;
      try {
        (videoEl as any).srcObject = null;
      } catch {
        console.warn('Failed to clear video srcObject');
      }
    }

    if (this.worker) {
      try {
        this.worker.onmessage = null as any;
      } catch {
        console.warn('Failed to clear worker onmessage');
      }
      try {
        this.worker.terminate();
      } catch {
        console.warn('Failed to terminate worker');
      }
      this.worker = null;
    }

    this.debugStats.workerReady = false;
  }

  zoomInterval: any = null;
  startZoomInterval(delta: number) {
    this.stopZoomInterval();
    this.updateZoom(delta);

    this.zoomInterval = setInterval(() => {
      this.updateZoom(delta);
    }, this.zoomStepRateMs);
  }

  stopZoomInterval() {
    clearInterval(this.zoomInterval);
  }

  updateZoom(delta: number) {
    this.zoomLevel = Math.min(Math.max(this.zoomLevel + delta, this.zoomMin), this.zoomMax);
    window.localStorage.setItem(this.ZOOM_STORE_KEY, this.zoomLevel.toString());
    this.applyZoom();
  }

  applyZoom() {
    // if (!this.zoomAvailable) {
    //   return;
    // }
    // this.cameraStream?.getVideoTracks().forEach((track) => {
    //   track.applyConstraints({
    //     advanced: [{ zoom: this.zoomLevel }]
    //   });
    // });
  }

  toggleTorch() {
    this.torchEnabled = !this.torchEnabled;
    this.cameraStream?.getVideoTracks().forEach((track) => {
      track.applyConstraints({
        advanced: [{ torch: this.torchEnabled }]
      });
    });
  }

  // Debug actions (only visible when unlocked)
  toggleAutoDownscale() {
    this.detectAutoDownscale = !this.detectAutoDownscale;
    this.debugStats.autoDownscale = this.detectAutoDownscale;
    this.snapshotStats();
  }

  setMaxDetectPixels(pixels: number) {
    this.detectMaxPixels = pixels;
    this.debugStats.maxDetectPixels = pixels;
    this.snapshotStats();
  }

  toggleRoi() {
    this.useCenterRoi = !this.useCenterRoi;
    this.debugStats.roiEnabled = this.useCenterRoi;
    this.snapshotStats();
  }

  async restartAt(height: 720 | 1080 | 1440 | 800 | 600) {
    const aspect = 9 / 16; // your negotiated is often 720x1280 portrait; keep close
    const width = Math.round(height * aspect);
    // Force exact size for experimentation
    this.debugForceSize = { width, height };

    // Clean shutdown and fresh session
    this.closeScanner(true);
    await this.sleep(50);
    this.initScanner();
  }

  copyStatsToClipboard() {
    try {
      const text = JSON.stringify(this.debugStats, null, 2);
      navigator.clipboard.writeText(text);
    } catch {
      console.warn('Failed to copy stats to clipboard');
    }
  }

  @Watch('enabled', { immediate: false })
  onEnabledChange() {
    if (this.enabled) {
      if ('BarcodeDetector' in window) {
        this.initScanner();
      } else {
        this.emitFailedToLoad('BarcodeDetector not supported in this browser.');
      }
    } else {
      this.closeScanner(true);
    }
  }

  @Emit('partNoDetected')
  emitPartNoDetected(partNo: string) {
    return partNo;
  }

  @Emit('amsOrderNoDetected')
  emitAMSOrderDetected(OrderNo: string) {
    return OrderNo;
  }

  @Emit('failedToLoad')
  emitFailedToLoad(error: any) {
    console.error(error);
    return error;
  }

  validateCode(code: string) {
    // the scanned will be wrong on occasion (containing garbage symbols), so we validate it to ensure we aren't annoying the user
    if (!code.toUpperCase().match(/^[0-9A-Z\s\-.]+$/)) {
      return;
    }

    // If it contains only numbers and a decimal, it's an AMS order number
    if (code.match(/^[0-9]+.[0-9]+$/)) {
      this.emitAMSOrderDetected(code);
    } else {
      this.emitPartNoDetected(code);
    }
  }
}
